import React, { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { api } from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Badge } from "../components/ui/badge";
import { Calendar, Clock, ArrowRight, Search as SearchIcon } from "lucide-react";

export default function Blog() {
  const [posts, setPosts] = useState([]);
  const [cats, setCats] = useState([]);
  const [params, setParams] = useSearchParams();
  const activeCat = params.get("category") || "";
  const q = params.get("q") || "";
  const [searchInput, setSearchInput] = useState(q);
  const [loading, setLoading] = useState(true);

  useEffect(() => { api.get("/blog/categories").then(r => setCats(r.data)); }, []);
  useEffect(() => {
    setLoading(true);
    const p = { limit: 40 };
    if (activeCat) p.category = activeCat;
    if (q) p.q = q;
    api.get("/blog/posts", { params: p }).then(r => setPosts(r.data)).finally(() => setLoading(false));
  }, [activeCat, q]);

  const featured = posts.find(p => p.featured) || posts[0];
  const rest = posts.filter(p => p.id !== featured?.id);

  const submitSearch = (e) => {
    e.preventDefault();
    const next = new URLSearchParams(params);
    if (searchInput) next.set("q", searchInput); else next.delete("q");
    setParams(next);
  };

  return (
    <div className="max-w-7xl mx-auto px-4 md:px-6 py-10">
      <div className="text-xs uppercase tracking-[0.2em] font-bold text-amber-600">AuctionBulls Blog</div>
      <h1 className="mt-2 text-3xl md:text-5xl font-extrabold display-serif">Insights, guides & auction news</h1>
      <p className="mt-3 text-slate-600 dark:text-slate-400 max-w-2xl">Buying and selling knowledge for property, businesses, cars, bikes and collectibles.</p>

      {/* Search & category filter */}
      <div className="mt-8 flex flex-wrap items-center gap-3">
        <form onSubmit={submitSearch} className="relative">
          <SearchIcon className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <Input data-testid="blog-search-input" value={searchInput} onChange={e => setSearchInput(e.target.value)}
                 placeholder="Search articles..." className="pl-9 w-72" />
        </form>
        <div className="flex flex-wrap gap-2">
          <button onClick={() => { const n = new URLSearchParams(params); n.delete("category"); setParams(n); }}
            className={`px-3 py-1.5 rounded-full text-xs font-semibold border ${!activeCat ? "bg-slate-900 text-white border-slate-900" : "bg-white dark:bg-slate-900 border-slate-200 dark:border-slate-700"}`}>
            All
          </button>
          {cats.map(c => (
            <button key={c.category} data-testid={`blog-cat-${c.category}`}
              onClick={() => { const n = new URLSearchParams(params); n.set("category", c.category); setParams(n); }}
              className={`px-3 py-1.5 rounded-full text-xs font-semibold border ${activeCat === c.category ? "bg-slate-900 text-white border-slate-900" : "bg-white dark:bg-slate-900 border-slate-200 dark:border-slate-700"}`}>
              {c.category} <span className="opacity-60">({c.count})</span>
            </button>
          ))}
        </div>
      </div>

      {/* Featured */}
      {featured && !q && !activeCat && (
        <Link data-testid="blog-featured" to={`/blog/${featured.slug}`}
              className="mt-10 grid md:grid-cols-2 gap-6 items-center group rounded-3xl border border-slate-200 dark:border-slate-800 overflow-hidden bg-white dark:bg-slate-900">
          {featured.featured_image && (
            <div className="aspect-[4/3] md:aspect-auto md:h-full overflow-hidden">
              <img src={featured.featured_image} className="h-full w-full object-cover group-hover:scale-105 transition duration-500" alt="" />
            </div>
          )}
          <div className="p-6 md:p-10">
            <div className="text-xs uppercase tracking-[0.2em] font-bold text-amber-600">{featured.category}</div>
            <h2 className="mt-3 text-2xl md:text-3xl font-extrabold display-serif leading-tight">{featured.title}</h2>
            <p className="mt-3 text-slate-600 dark:text-slate-400">{featured.excerpt}</p>
            <div className="mt-4 flex items-center gap-4 text-xs text-slate-500">
              <span className="inline-flex items-center gap-1"><Calendar className="h-3 w-3" /> {formatDate(featured.published_at)}</span>
              <span className="inline-flex items-center gap-1"><Clock className="h-3 w-3" /> {featured.reading_time} min read</span>
            </div>
            <Button size="sm" className="mt-5 bg-slate-900 text-white gap-1">Read article <ArrowRight className="h-4 w-4" /></Button>
          </div>
        </Link>
      )}

      {/* Grid */}
      <div className="mt-10 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
        {(featured && !q && !activeCat ? rest : posts).map(p => <PostCard key={p.id} post={p} />)}
        {!loading && posts.length === 0 && <div className="col-span-full text-center text-slate-500 py-16">No articles found.</div>}
      </div>
    </div>
  );
}

export function PostCard({ post }) {
  return (
    <Link data-testid="blog-card" to={`/blog/${post.slug}`}
          className="group flex flex-col rounded-2xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 overflow-hidden hover:shadow-xl hover:-translate-y-1 transition-all duration-300">
      {post.featured_image && (
        <div className="aspect-[4/3] overflow-hidden bg-slate-100 dark:bg-slate-800">
          <img src={post.featured_image} alt={post.title} className="h-full w-full object-cover group-hover:scale-105 transition duration-500" />
        </div>
      )}
      <div className="p-5 flex flex-col flex-1">
        <div className="flex items-center gap-2 text-xs">
          <Badge className="bg-amber-100 text-amber-800 border-amber-200 hover:bg-amber-100">{post.category}</Badge>
        </div>
        <h3 className="mt-3 text-lg font-bold leading-snug line-clamp-2 text-slate-900 dark:text-white">{post.title}</h3>
        <p className="mt-2 text-sm text-slate-600 dark:text-slate-400 line-clamp-2">{post.excerpt}</p>
        <div className="mt-auto pt-4 flex items-center justify-between text-[11px] text-slate-500">
          <span className="inline-flex items-center gap-1"><Calendar className="h-3 w-3" /> {formatDate(post.published_at)}</span>
          <span className="inline-flex items-center gap-1"><Clock className="h-3 w-3" /> {post.reading_time} min</span>
        </div>
      </div>
    </Link>
  );
}

function formatDate(iso) {
  if (!iso) return "";
  return new Date(iso).toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" });
}
